/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { competitorPages, getCompetitorBySlug } from '../../../mocks/competitorPages';
import { useSEOHead, SITE_URL } from '../../../hooks/useSEOHead';
import Breadcrumb from '../../../components/feature/Breadcrumb';

export default function VsLumsToyotaPage() {
  const pageData = competitorPages.find(p => p.slug === 'lums-toyota');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useSEOHead({
    title: pageData?.seo.title ?? '',
    description: pageData?.seo.description ?? '',
    keywords: pageData?.seo.keywords ?? '',
    canonicalUrl: `${SITE_URL}/vs/lums-toyota`,
    geoRegion: 'US-OR',
    geoPlacename: 'Warrenton, Oregon',
    geoPosition: '46.1651;-123.9237',
  });

  if (!pageData) return null;

  // Get other competitors for the comparison section
  const otherCompetitors = competitorPages
    .filter(c => c.slug !== 'lums-toyota')
    .map(c => c.slug)
    .slice(0, 3);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(formData as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <>
      <Breadcrumb 
        items={[
          { label: 'Compare', path: '/compare' },
          { label: "vs Lum's Toyota" }
        ]} 
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/171e014d790cc5d1f18b91064713e079.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
              COMPETITOR COMPARISON
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Lum's Toyota Sells Toyotas.<br />
              <span className="text-red-500">We Sell Solutions.</span>
            </h1>
            <p className="text-2xl mb-4 text-slate-300">
              What if the best car for your family isn't a Toyota? At their lot, you'd need to visit a different dealership.
            </p>
            <p className="text-xl mb-8 text-slate-400">
              We broker every make and model across Oregon and Washington — with zero brand loyalty, zero pressure, and savings that average $3,800–$9,500 per deal.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="#consultation" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
                Get Free Savings Estimate
              </a>
              <a href="#comparison" className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer">
                See the Difference
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Side-by-Side Comparison */}
      <section id="comparison" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Head-to-Head Comparison</h2>
          <p className="text-center text-slate-600 mb-12 text-lg">See why Pacific Northwest residents choose Rockhound Auto</p>
          
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="p-4 text-left font-semibold text-slate-700 border-b-2 border-slate-300">Feature</th>
                  <th className="p-4 text-center font-semibold text-red-600 border-b-2 border-red-300 bg-red-50">Rockhound Auto</th>
                  <th className="p-4 text-center font-semibold text-slate-600 border-b-2 border-slate-300">Lums Toyota</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Vehicle Selection</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">All Makes & Models</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">Toyota Only</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Financing Rates</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">Best Available Rate</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">May Include Markup</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Trade-In Values</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">Fair Market Value</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">Single Offer</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Pricing Transparency</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">Full Disclosure</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">Fees at Signing</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Inventory Access</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">Regional Network</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">Limited Lot</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium">Negotiation</td>
                  <td className="p-4 text-center bg-red-50">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                      <span className="font-semibold">We Negotiate For You</span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <i className="ri-close-line text-2xl text-red-600"></i>
                      <span className="text-slate-600">You Negotiate Alone</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Real Pricing Breakdown */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Real Pricing Example</h2>
          <p className="text-center text-slate-600 mb-12 text-lg">2024 Toyota RAV4 XLE Premium AWD</p>
          
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Lums Toyota Pricing */}
            <div className="bg-white rounded-xl shadow-lg p-8 border-2 border-slate-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Lums Toyota</h3>
                <p className="text-slate-600">Traditional Dealership</p>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">MSRP</span>
                  <span className="font-semibold">$37,239</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Market Adjustment</span>
                  <span className="font-semibold text-red-600">+$3,500</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Doc Fees</span>
                  <span className="font-semibold">+$699</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Dealer Add-Ons</span>
                  <span className="font-semibold">+$2,195</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Extended Warranty</span>
                  <span className="font-semibold">+$2,800</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-200">
                  <span className="text-slate-600">Financing Rate Markup</span>
                  <span className="font-semibold text-red-600">+$1,850 (over loan)</span>
                </div>
                <div className="flex justify-between py-3 bg-slate-100 px-4 rounded-lg mt-4">
                  <span className="font-bold text-lg">Total Price</span>
                  <span className="font-bold text-2xl text-slate-800">$48,283</span>
                </div>
              </div>
            </div>

            {/* Rockhound Auto Pricing */}
            <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl shadow-xl p-8 text-white border-4 border-red-500 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-2 rounded-full font-bold text-sm whitespace-nowrap">
                SAVE $9,544
              </div>
              
              <div className="text-center mb-6 mt-2">
                <h3 className="text-2xl font-bold mb-2">Rockhound Auto</h3>
                <p className="text-red-100">Auto Broker Service</p>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">MSRP</span>
                  <span className="font-semibold">$37,239</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Negotiated Discount</span>
                  <span className="font-semibold text-green-300">-$2,750</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Broker Fee</span>
                  <span className="font-semibold">+$500</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Doc Fees</span>
                  <span className="font-semibold">+$250</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Dealer Add-Ons</span>
                  <span className="font-semibold text-green-300">$0</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Unwanted Warranties</span>
                  <span className="font-semibold text-green-300">$0</span>
                </div>
                <div className="flex justify-between py-2 border-b border-red-400">
                  <span className="text-red-100">Rate Markup</span>
                  <span className="font-semibold text-green-300">$0</span>
                </div>
                <div className="flex justify-between py-3 bg-white text-slate-900 px-4 rounded-lg mt-4">
                  <span className="font-bold text-lg">Total Price</span>
                  <span className="font-bold text-2xl">$38,739</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-3xl font-bold text-red-600 mb-4">You Save: $9,544</p>
            <p className="text-slate-600 text-lg">That's more than a year of car payments or a down payment on your next vehicle!</p>
          </div>
        </div>
      </section>

      {/* Customer Win Stories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Real People Who Switched</h2>
          <p className="text-center text-slate-600 mb-12 text-lg">Customers who chose Rockhound Auto for their Toyota purchase</p>
          
          <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-slate-50 rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
              </div>
              <p className="text-slate-700 mb-6 italic">
                "I was quoted $48,000 for a RAV4 Hybrid and offered $9,000 for my trade-in. Rockhound Auto got me the same RAV4 for $41,500 and $13,200 for my trade. That's $10,700 in total savings!"
              </p>
              <div className="border-t border-slate-300 pt-4">
                <p className="font-bold text-slate-800">Robert K.</p>
                <p className="text-slate-600 text-sm">Tillamook, OR</p>
                <p className="text-red-600 font-semibold mt-2">Saved: $10,700</p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-slate-50 rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
              </div>
              <p className="text-slate-700 mb-6 italic">
                "The financing rate I was offered was 7.9%. Rockhound Auto got me 5.4% on the same loan. Over 60 months, that's $1,850 in interest savings alone. Plus I saved $3,200 on the purchase price of my Tacoma."
              </p>
              <div className="border-t border-slate-300 pt-4">
                <p className="font-bold text-slate-800">Sarah T.</p>
                <p className="text-slate-600 text-sm">Forest Grove, OR</p>
                <p className="text-red-600 font-semibold mt-2">Saved: $5,050</p>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-slate-50 rounded-xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                ))}
              </div>
              <p className="text-slate-700 mb-6 italic">
                "I wanted a Toyota 4Runner, but there was a 3-month wait and a $5,000 markup. Rockhound Auto found one in Portland within a week at MSRP minus $1,200. No waiting, no markup, just honest service."
              </p>
              <div className="border-t border-slate-300 pt-4">
                <p className="font-bold text-slate-800">Michael P.</p>
                <p className="text-slate-600 text-sm">Kelso, WA</p>
                <p className="text-red-600 font-semibold mt-2">Saved: $6,200</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Know Before You Visit */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-lightbulb-line"></i> Buyer Education
            </div>
            <h2 className="text-4xl font-bold mb-4">What to Know Before You Visit</h2>
            <p className="text-slate-400 text-lg">
              Understanding common dealership practices helps you make informed decisions
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-slate-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-car-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Limited to Toyota Only</h3>
              <p className="text-slate-300 leading-relaxed">
                What if you want a Honda, Subaru, Ford, or Chevrolet? Single-brand dealerships can only show you one option. Rockhound Auto gives you access to <strong>every make and model</strong> from dealers across Oregon and Washington, including Astoria, Seaside, Hillsboro, and Longview.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-percent-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Financing Rate Markup</h3>
              <p className="text-slate-300 leading-relaxed">
                Many dealerships mark up financing rates by 1.5-2.5%. If the bank approves you at 5.4%, you might be charged 7.9%. That's <strong>$1,500-$2,500 extra</strong> over your loan term. We get you the actual rate with zero markup.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Trade-In Valuation</h3>
              <p className="text-slate-300 leading-relaxed">
                Single-dealer trade-in offers can be $2,500-$4,500 below market value. We leverage competition between dealers in Warrenton, Astoria, Tillamook, and beyond to get you <strong>fair market value</strong> — often $3,000-$5,000 more.
              </p>
            </div>

            <div className="bg-slate-800 rounded-xl p-8">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3">Market Adjustments & Add-Ons</h3>
              <p className="text-slate-300 leading-relaxed">
                $3,000-$5,000 "market adjustments" on popular models like RAV4 Hybrid and Tacoma are common. Plus paint protection, nitrogen tire fill, and VIN etching can add another $2,000-$3,000. We eliminate these with <strong>transparent pricing</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Free Consultation Form */}
      <section id="consultation" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Get Your Free Savings Estimate</h2>
              <p className="text-slate-600 text-lg">
                Tell us what you're looking for and we'll show you exactly how much you'll save
              </p>
            </div>

            <form 
              className="bg-slate-50 rounded-xl p-8 shadow-lg" 
              data-readdy-form
              action="https://readdy.ai/api/form/d6u27upqcrg2vc5aq06g"
              onSubmit={handleSubmit}
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-slate-700 font-semibold mb-2">First Name</label>
                  <input 
                    type="text" 
                    name="first_name"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-2">Last Name</label>
                  <input 
                    type="text" 
                    name="last_name"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-slate-700 font-semibold mb-2">Email</label>
                  <input 
                    type="email" 
                    name="email"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-semibold mb-2">Phone</label>
                  <input 
                    type="tel" 
                    name="phone"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-slate-700 font-semibold mb-2">What vehicle are you interested in?</label>
                <input 
                  type="text" 
                  name="vehicle_interest"
                  placeholder="e.g., 2024 Toyota RAV4, Honda CR-V, Subaru Outback"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-slate-700 font-semibold mb-2">Do you have a trade-in?</label>
                <select 
                  name="has_trade_in"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-slate-700 font-semibold mb-2">Your Location</label>
                <select 
                  name="location"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="warrenton">Warrenton, OR</option>
                  <option value="astoria">Astoria, OR</option>
                  <option value="seaside">Seaside, OR</option>
                  <option value="longview">Longview, WA</option>
                  <option value="kelso">Kelso, WA</option>
                  <option value="tillamook">Tillamook, OR</option>
                  <option value="forest-grove">Forest Grove, OR</option>
                  <option value="hillsboro">Hillsboro, OR</option>
                  <option value="long-beach">Long Beach, WA</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-slate-700 font-semibold mb-2">Additional Details (Optional)</label>
                <textarea 
                  name="additional_details"
                  rows={4}
                  maxLength={500}
                  placeholder="Any specific requirements or questions?"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                ></textarea>
              </div>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Thank you! We'll respond within 2 hours during business hours.
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Something went wrong. Please try again or call us directly.
                </div>
              )}

              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-lg transition text-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Submitting...' : 'Get My Free Savings Estimate'}
              </button>

              <p className="text-center text-slate-500 text-sm mt-4">
                We'll respond within 2 hours during business hours
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Compare to Other Competitors */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Compare Us to Other Dealerships
            </h2>
            <p className="text-lg text-gray-600">
              See how Rockhound Auto stacks up against other local dealers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {otherCompetitors.map((slug) => {
              const comp = getCompetitorBySlug(slug);
              if (!comp) return null;
              return (
                <Link
                  key={slug}
                  to={`/vs/${slug}`}
                  className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition border border-gray-200 hover:border-teal-500"
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    vs {comp.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{comp.location}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`${
                            i < Math.floor(comp.starRating)
                              ? 'ri-star-fill text-yellow-400'
                              : 'ri-star-line text-gray-300'
                          } text-sm`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">{comp.starRating}/5</span>
                  </div>
                  <div className="text-sm text-gray-700 mb-4">
                    <strong>Average Markup:</strong> {comp.averageMarkup}
                  </div>
                  <div className="flex items-center gap-2 text-teal-600 font-semibold text-sm">
                    View Comparison
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/compare"
              className="inline-block px-8 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition whitespace-nowrap"
            >
              View Full Comparison Page
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Save $3,800-$9,500?
          </h2>
          <p className="text-xl mb-8 text-red-100 max-w-3xl mx-auto">
            Stop limiting yourself to Toyota. Get access to every make and model with better financing rates and transparent pricing. Serving Warrenton, Astoria, Seaside, Longview, and the entire Pacific Northwest.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="#consultation"
              className="bg-white hover:bg-slate-100 text-red-600 px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap"
            >
              Get Free Savings Estimate
            </a>
            <a 
              href="tel:+18554686348"
              className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
